<script setup lang="ts">
import { authState } from "@/ui-plus/useAuth";
import { Button } from '@/components/ui/button'
import { AutoForm } from '@/ui-plus/auto-form'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { z } from "zod";
import { showTurnstile } from "@/ui-plus/dialog/showTurnstile";
import { useFormPlus } from '@/ui-plus/form/useFormPlus'
import { ArrowLeftIcon, Loader2 } from 'lucide-vue-next'
import { dashboardApiClient } from '@api-client/dashboard';
import { loadMyWebsites, myWebsites } from '@partner-spa-vue/stores/MyWebsitesStore';
import { toast } from '@/ui-plus/sonner';
import HeaderOnly from '@partner-spa-vue/layouts/HeaderOnly.vue';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useRouter } from 'vue-router';
import { showPhoneValidationForm } from "@partner-spa-vue/utils/phoneValidation";
import { showNameForm } from "@partner-spa-vue/utils/nameValidation";
import { showIdVerificationForm } from "@partner-spa-vue/utils/IdVerification";
import { startRefreshingTokens } from '@api-client/auth/auth';
import { watch } from 'vue';

const router = useRouter();

const schema = z.object({
    name: z.string().min(1, { message: "min 1" }).max(510, { message: "max 510" }),
})

const form = useForm({
    validationSchema: toTypedSchema(schema),
})

const { isLoading, globalError, handleError, withSubmit } = useFormPlus(form);

async function onSubmit(values: z.infer<typeof schema>) {
    if (!authState.value?.accessTokenPayload?.hasVerifiedPhoneNumber) {
        const result = await showPhoneValidationForm()
        if (result === 1) {
            await startRefreshingTokens();
            toast.success('Phone number verified');
        } else {
            toast.error('Phone number verification failed');
            return;
        }
    }
    if (!authState.value?.accessTokenPayload?.name) {
        const result = await showNameForm()
        if (result === 1) {
            await startRefreshingTokens();
            toast.success('Name saved');
        } else {
            toast.error('Name is required');
            return;
        }
    }
    if (!authState.value?.accessTokenPayload?.hasVerifiedId) {
        const result = await showIdVerificationForm()
        if (result === 1) {
            await startRefreshingTokens();
            toast.success('ID verification successful');
        } else {
            toast.error('ID verification failed');
            return;
        }
    }
    let turnstile;
    try {
        turnstile = await showTurnstile(import.meta.env.PUBLIC_TURNSTILE_SITE_KEY);
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        toast.error(errorMessage);
        return;
    }
    await withSubmit(async () => {
        const { data, error } = await (await dashboardApiClient.website.create.$post({
            form: {
                name: values.name,
                turnstile,
                parentHostname: 'none'
            },
        })).json()
        if (error) {
            if (
                error.code === 'USER_PHONE_NUMBER_NOT_VERIFIED' ||
                error.code === 'USER_ID_NOT_VERIFIED'
            ) {
                await startRefreshingTokens();
                await onSubmit(values);
                return;
            } else {
                handleError(error, form);
            }
        } else {
            const websiteId = data.id;
            myWebsites.value = [...myWebsites.value, {
                id: websiteId,
                name: values.name,
                defaultHostname: null,
                isSupportTeamPartner: false,
                isMarketingTeamPartner: false,
            }];
            // redirect to the new website
            loadMyWebsites();
            router.push({ name: 'website-home', params: { websiteId } });
        }
    })
}

watch(() => form.values.name, v => console.log('form.values.name', v))

</script>

<template>
    <HeaderOnly>
        <Card class="max-w-md mx-auto">
            <CardHeader>
                <CardTitle>
                    <RouterLink :to="{ name: 'my-websites' }">
                        <Button variant="secondary"
                                class="w-full mb-4">
                            <ArrowLeftIcon /> Back
                        </Button>
                    </RouterLink>
                    Add New Website
                </CardTitle>
            </CardHeader>
            <CardContent>
                <AutoForm class="space-y-6"
                          :schema="schema"
                          :form="form"
                          @submit="onSubmit"
                          :field-config="{
                            name: {
                                label: 'Name',
                                description: 'Enter a name for your website',
                            },
                        }">
                    <div class="text-sm font-medium text-destructive"
                         v-if="globalError">
                        {{ globalError }}
                    </div>

                    <div>
                        <Button type="submit"
                                :disabled="isLoading"
                                class="w-full my-6">
                            <Loader2 v-if="isLoading"
                                     class="animate-spin" />
                            Create
                        </Button>
                    </div>
                </AutoForm>
                <div class="flex flex-col gap-2 mb-4 border p-4 rounded-md border-foreground">
                    <div>
                        You are creating a partner website.
                    </div>
                </div>
            </CardContent>
        </Card>

    </HeaderOnly>
</template>